import { FC, ReactNode } from "react";
import { Box, Typography } from "@mui/material";

import { ClaimAppeal } from "../../../../../types/claims";
import { formatDisplayDate, formatInstant } from "../../../../helpers/dataHelper";
import { ACTION_TYPE_LABELS, PAYER_TIER_LABELS } from "../claimAppeals";
import ClaimDocumentCard from "../record-payment/ClaimDocumentCard";
import InfoItem, { InfoSectionLabel } from "../InfoItem";

/**
 * Read-only view of one recorded action: payer (none for a correct and resubmit), date, who recorded it and when,
 * the comment, and its document (if any) to view in a new tab or download.
 */
const AppealDetail: FC<{
  appeal: ClaimAppeal;
  claimId: string;
  /** On a correct and resubmit: its link, or the way to make one (REV-2629). */
  resubmission?: ReactNode;
}> = ({ appeal, claimId, resubmission }) => {
  const { document } = appeal;

  return (
    <Box sx={{ p: 2, backgroundColor: "white" }}>
      {resubmission && <Box sx={{ mb: 2 }}>{resubmission}</Box>}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
          gap: 1.5,
        }}
      >
        {appeal.payerTier && (
          <InfoItem label="Payer">
            {PAYER_TIER_LABELS[appeal.payerTier] ?? appeal.payerTier}
          </InfoItem>
        )}
        <InfoItem label="Action date">
          {formatDisplayDate(appeal.actionOn)}
        </InfoItem>
        <InfoItem label="Type">
          {ACTION_TYPE_LABELS[appeal.actionType] ?? appeal.actionType}
        </InfoItem>
        <InfoItem label="Recorded">
          {formatInstant(appeal.createdAt)}
          {appeal.createdBy ? ` · by ${appeal.createdBy}` : ""}
        </InfoItem>
        <InfoItem label="Comment" fullWidth>
          {appeal.comment ? (
            <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
              {appeal.comment}
            </Box>
          ) : (
            <Box
              component="span"
              sx={{ color: "text.secondary", fontWeight: 400 }}
            >
              No comment
            </Box>
          )}
        </InfoItem>
      </Box>

      <Box sx={{ pt: 2 }}>
        <InfoSectionLabel>Document</InfoSectionLabel>
        {document ? (
          <ClaimDocumentCard document={document} claimId={claimId} />
        ) : (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", color: "text.secondary" }}
          >
            No document attached
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AppealDetail;
